import React, { useEffect, useState } from 'react'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useAuthPost } from '../hooks/useAxios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/primitives';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card } from '../components/primitives';
import PaymentMethod from '../components/PaymentMethod';
import { catchErrorParse, ERRORS } from '../ErrorUtility';
import useTimedError from '../hooks/useTimedError';
import { useTranslation } from '../hooks/useTranslation';
import ThreeDotsLoading from '../components/primitives/ThreeDotsLoading';
import Logo from '../assets/pplogo.png';
import { useDatabase } from '../contexts/DatabaseContext';

export default function Resubscribe() {
    const t = useTranslation();
    const { post } = useAuthPost();
    const { url } = useAuth();
    const { cardData, getCardData, clientData, mobile } = useOutletContext();
    const { createSubscriptionActionEvent } = useDatabase();

    const [useNewCard, setUseNewCard] = useState(!cardData || cardData.length === 0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useTimedError();

    const stripe = useStripe();
    const elements = useElements();

    const navigate = useNavigate();

    // Get invoice details from clientData
    const invoiceDetails = clientData?.failedInvoice || {};
    const formattedAmount = invoiceDetails.amount ? 
        new Intl.NumberFormat('en-US', { style: 'currency', currency: invoiceDetails.currency || 'USD' }).format(invoiceDetails.amount / 100) : 
        '';

    useEffect(() => {
        if (!cardData && clientData?.stripeCustomerId) {
            getCardData();
        }
        if (cardData?.length > 0) {
            setUseNewCard(false);
        }
    }, [cardData, getCardData, clientData?.stripeCustomerId]);

    useEffect(() => {
        if (clientData && !clientData.failedInvoice) {
            navigate('/');
        }
    }, [clientData, navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setLoading(true);
            if (!stripe || !elements) {
                window.dispatchEvent(new CustomEvent('masterError', {
                    detail: {
                        message: "Unable to load Stripe, please try again later.",
                        action: "resubscribe_failed_invoice",
                        diabolical: true,
                    }
                }));
                setLoading(false);
                return;
            }
        
            let paymentMethodId = useNewCard ? null : cardData[0]?.id;
        
            if (!paymentMethodId) {
                // Using PaymentElement
                const { error: submitError } = await elements.submit();
                if (submitError) {
                    console.error('Error submitting PaymentElement:', submitError);
                    setError(submitError.message);
                    setLoading(false);
                    return;
                }
        
                const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
                    elements,
                });
        
                if (paymentMethodError) {
                    console.error('Error creating PaymentMethod:', paymentMethodError);
                    setError(paymentMethodError.message);
                    setLoading(false);
                    return;
                }
        
                paymentMethodId = paymentMethod.id;
            }

            // Retry the failed invoice payment
            const [responseData, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/invoices/retry`, {
                stripeCustomerId: clientData.stripeCustomerId,
                paymentMethodId,
                invoiceId: invoiceDetails.invoiceId
            });

            if (error) {
                if (errorCode === ERRORS.STRIPE_FAILED_PAYMENT_ERROR) {
                    setError(errorMessage);
                    setLoading(false);
                    return;
                }
                window.dispatchEvent(new CustomEvent('masterError', {
                    detail: {
                        code: errorCode,
                        message: errorMessage ? errorMessage : error.message,
                        action: "resubscribe_failed_invoice",
                        diabolical: true,
                    }
                }));
                setLoading(false);
                return;
            }

            console.log('Invoice payment:', responseData);
            navigate("/");
            createSubscriptionActionEvent("resubscribe_failed_invoice", responseData.latest_invoice.total);
        } catch (error) {
            console.error('Error submitting form:', error);
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: error.message,
                    action: "resubscribe_failed_invoice",
                    diabolical: true,
                }
            }));
            setLoading(false);
        }
    };
    
    return (
        <div className="auth-page">
            <div className="auth-outer-container">
                <div className="logo-group">
                    <div className="logo-group-inner">
                        <img className="logo-signin" src={Logo} alt="Page Parrot Logo"/>
                        <h2 className="logo-heading w-900 text-center">{t('Page Parrot')}</h2>
                    </div>
                </div>
            <Card style={{ minWidth: '260px', margin: '0 20px 20px 20px' }}>
                <h2 className="text-center">Update Payment Method</h2>
                <p className="text-center">
                    {formattedAmount ? 
                        `Please update your payment method to pay the outstanding invoice of ${formattedAmount}` : 
                        'Please update your payment method to continue your subscription'}
                </p>
                
                {(!stripe || !elements) ? <ThreeDotsLoading />
                :
                useNewCard ?
                    <>
                        <form onSubmit={handleSubmit}>
                            <PaymentElement />
                            <Button buttonType='submit settings' disabled={loading || !stripe} loading={loading} error={error}>
                                {error ? error : `Pay Now${formattedAmount ? `: ${formattedAmount}` : ''}`}
                            </Button>
                        </form>
                        {cardData?.length > 0 && 
                            <Button buttonType='settings' onClick={() => setUseNewCard(false)} disabled={loading}>
                                {t('Use Existing Card')}
                            </Button>
                        }
                    </>
                    :
                    <>
                        <form onSubmit={handleSubmit}>
                            <PaymentMethod paymentMethod={cardData[0]} columnDisplay={mobile} />
                            <Button buttonType='submit settings' disabled={loading} loading={loading} error={error}>
                                {error ? error : `Pay Now${formattedAmount ? `: ${formattedAmount}` : ''}`}
                            </Button>
                        </form>
                        <Button buttonType='settings' onClick={() => setUseNewCard(true)} disabled={loading}>
                            {t('New Payment Method')}
                        </Button>
                    </>}
                </Card>
            </div>
        </div>
    );
}